import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast, Toaster } from "sonner";
import { Mail, Phone, MapPin, User, Building2, Send, Loader2 } from "lucide-react";
import useSEO from "../hooks/useSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, lang } = useLang();

  useSEO({
    title: lang === "pl" ? "Kontakt — LK LOG" : "Contact — LK LOG",
    description:
      lang === "pl"
        ? "Skontaktuj się z LK LOG. ul. Nieszawska 77, 87-720 Ciechocinek. Przemysław Chudy +48 660 341 213, Karol Lewandowski +48 501 613 583."
        : "Contact LK LOG. ul. Nieszawska 77, 87-720 Ciechocinek, Poland. Przemysław Chudy +48 660 341 213, Karol Lewandowski +48 501 613 583.",
    path: "/kontakt",
    lang,
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("contact.validation"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        form_type: "contact",
        lang,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const people = [
    {
      name: t("contact.p1_name"),
      role: t("contact.p1_role"),
      phone: t("contact.p1_phone"),
      email: t("contact.p1_email"),
      phoneHref: "tel:+48660341213",
      photo:
        "https://customer-assets.emergentagent.com/job_lklog-partner/artifacts/vpw07or8_cv.jpg",
    },
    {
      name: t("contact.p2_name"),
      role: t("contact.p2_role"),
      phone: t("contact.p2_phone"),
      email: t("contact.p2_email"),
      phoneHref: "tel:+48501613583",
      photo:
        "https://customer-assets.emergentagent.com/job_lklog-partner/artifacts/ticnx282_image.png",
    },
  ];

  return (
    <main data-testid="contact-page" className="bg-[#0A0A0A] pt-32 pb-20 min-h-screen">
      <Toaster position="top-center" theme="dark" richColors />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-divider" />
            <span className="label-gold">{t("contact.label")}</span>
          </div>
          <h1
            data-testid="contact-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.05] tracking-tight mb-6"
          >
            {t("contact.title")}
          </h1>
          <p className="text-neutral-400 text-base lg:text-lg leading-relaxed">
            {t("contact.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 p-8 md:p-10 bg-[#161618] border border-white/10 rounded-sm"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <Label htmlFor="name" className="text-neutral-300 text-xs tracking-widest uppercase">
                  {t("contact.name")} *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-input-name"
                  className="mt-2 bg-[#0A0A0A] border-white/10 text-white focus:border-[#F2B705] rounded-sm h-12"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-neutral-300 text-xs tracking-widest uppercase">
                  {t("contact.company")}
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  data-testid="contact-input-company"
                  className="mt-2 bg-[#0A0A0A] border-white/10 text-white focus:border-[#F2B705] rounded-sm h-12"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-neutral-300 text-xs tracking-widest uppercase">
                  {t("contact.email")} *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-input-email"
                  className="mt-2 bg-[#0A0A0A] border-white/10 text-white focus:border-[#F2B705] rounded-sm h-12"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-neutral-300 text-xs tracking-widest uppercase">
                  {t("contact.phone")}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="contact-input-phone"
                  className="mt-2 bg-[#0A0A0A] border-white/10 text-white focus:border-[#F2B705] rounded-sm h-12"
                />
              </div>
            </div>
            <div className="mb-6">
              <Label htmlFor="message" className="text-neutral-300 text-xs tracking-widest uppercase">
                {t("contact.message")} *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                rows={6}
                placeholder={t("contact.placeholder_message")}
                data-testid="contact-input-message"
                className="mt-2 bg-[#0A0A0A] border-white/10 text-white focus:border-[#F2B705] rounded-sm"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="w-full sm:w-auto bg-[#F2B705] hover:bg-[#FFC933] text-[#0A0A0A] font-semibold rounded-sm px-8 py-6"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  {t("contact.sending")}
                </>
              ) : (
                <>
                  <Send className="mr-2 w-4 h-4" strokeWidth={2} />
                  {t("contact.submit")}
                </>
              )}
            </Button>
          </motion.form>

          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="p-8 bg-[#161618] border border-white/10 rounded-sm">
              <div className="label-gold mb-6">{t("contact.people_title")}</div>
              <div className="space-y-6">
                {people.map((p, i) => (
                  <div
                    key={i}
                    data-testid={`contact-person-${i}`}
                    className="group flex gap-5 pb-6 last:pb-0 border-b last:border-b-0 border-white/10"
                  >
                    <div className="relative w-24 h-28 shrink-0 overflow-hidden rounded-sm border border-[#F2B705]/30 bg-[#0A0A0A]">
                      <img
                        src={p.photo}
                        alt={p.name}
                        className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 to-transparent pointer-events-none" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-semibold text-white leading-tight mb-1">
                        {p.name}
                      </div>
                      <div className="text-xs text-[#F2B705] tracking-wide uppercase mb-3">
                        {p.role}
                      </div>
                      <div className="space-y-1.5">
                        <a
                          href={p.phoneHref}
                          className="flex items-center gap-2 text-sm text-neutral-300 hover:text-[#F2B705] transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5 text-[#F2B705]" strokeWidth={1.5} />
                          {p.phone}
                        </a>
                        <a
                          href={`mailto:${p.email}`}
                          className="flex items-center gap-2 text-sm text-neutral-300 hover:text-[#F2B705] transition-colors break-all"
                        >
                          <Mail className="w-3.5 h-3.5 text-[#F2B705] shrink-0" strokeWidth={1.5} />
                          {p.email}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-8 bg-[#161618] border border-white/10 rounded-sm">
              <div className="label-gold mb-5">{t("contact.address_title")}</div>
              <div className="flex items-start gap-3 text-neutral-300">
                <MapPin className="w-4 h-4 text-[#F2B705] mt-1 shrink-0" strokeWidth={1.5} />
                <div className="text-sm leading-relaxed">
                  <div className="text-white font-medium mb-1 flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-[#F2B705]" strokeWidth={1.5} />
                    {t("contact.address_name")}
                  </div>
                  {t("contact.address_line1")}<br />
                  {t("contact.address_line2")}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
}
